import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  Field,
  FieldLabel,
  InfoTip,
  Separator,
  Skeleton,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@repo/ui/components'
import {
  ClockPlus,
  EllipsisVertical,
  ExternalLink,
  Eye,
  Info,
  LucideIcon,
  Pencil,
  TriangleAlert,
} from '@repo/ui/icons'
import { cn } from '@repo/ui/lib/utils'
import { ComponentProps, PropsWithChildren, ReactNode } from 'react'
import { Link } from '../adapters/next'
import {
  adornmentBaseClassName,
  adornmentClassName,
  CopyButton,
  fieldRevealClassName,
} from './copy-button.tsx'

type Action = {
  disabled?: boolean
  icon: LucideIcon
  label: string
  onClick: () => void
  variant?: 'destructive'
}

export const DetailsField = ({
  actions,
  actionsLabel,
  children,
  copyValue,
  footer,
  href,
  label,
  loading = false,
  onEdit,
  onSetNow,
  onView,
  required = false,
  setNowDisabled = false,
  setNowLabel,
  target,
  tooltip,
  withWarning = false,
}: PropsWithChildren<{
  actions?: Action[]
  actionsLabel?: string
  copyValue?: null | string
  /** Rendered under the value, in the same column, for example a notice. */
  footer?: ReactNode
  href?: null | string | undefined
  label?: string
  loading?: boolean
  onEdit?: () => void
  onSetNow?: () => void
  onView?: () => void
  required?: boolean
  setNowDisabled?: boolean
  setNowLabel?: string
  target?: ComponentProps<'a'>['target']
  tooltip?: string
  withWarning?: boolean
}>) => {
  return (
    <Field className={'min-w-0 gap-1 text-base'} data-sentry-element="Field" data-sentry-component="DetailsField" data-sentry-source-file="details-field.tsx">
      {label && (
        <div className="flex min-w-0 items-center gap-2">
          <FieldLabel className={'min-w-0 text-sm font-bold text-foreground'}>
            <span className="flex min-w-0 flex-1 items-center gap-0.5">
              <span className="truncate">{label}</span>
              <span className="shrink-0">{required && '*'}</span>
            </span>
            {withWarning && (
              <TriangleAlert
                aria-label="Warning"
                className="shrink-0 text-muted-foreground"
                role="img"
                size={20}
              />
            )}
            {copyValue && <CopyButton value={copyValue} />}
            {onView && (
              <Eye
                aria-label="View"
                className={adornmentClassName}
                onClick={onView}
                role="button"
                size={20}
                tabIndex={0}
              />
            )}
            {onEdit && (
              <Pencil
                aria-label="Edit"
                className={adornmentClassName}
                onClick={onEdit}
                role="button"
                size={20}
                tabIndex={0}
              />
            )}
          </FieldLabel>
          {onSetNow && (
            <button
              aria-label={setNowLabel ?? 'Set to now'}
              className={cn('inline-flex size-5 shrink-0', adornmentClassName)}
              disabled={setNowDisabled}
              onClick={onSetNow}
              type="button"
            >
              <ClockPlus size={20} />
            </button>
          )}
          {href && <ExternalLinkTooltip href={href} target={target} />}
          {actions && actions.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger
                aria-label={actionsLabel ?? 'Actions'}
                className={cn(adornmentClassName, 'data-popup-open:opacity-100')}
              >
                <EllipsisVertical className="shrink-0" size={20} />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {actionsLabel && (
                  <>
                    <DropdownMenuLabel>{actionsLabel}</DropdownMenuLabel>
                    <Separator />
                  </>
                )}
                {actions.map(action => (
                  <DropdownMenuItem
                    disabled={action.disabled}
                    key={action.label}
                    onClick={action.onClick}
                    variant={action.variant}
                  >
                    <action.icon /> {action.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {tooltip && <LabelInfoTooltip content={tooltip} />}
        </div>
      )}
      <div className="min-w-0 text-lg">
        {loading ? <Skeleton className={'h-6 w-full'} /> : children}
      </div>
      {footer}
    </Field>
  )
}

export const LabelInfoTooltip = ({ content }: { content: string }) => (
  <InfoTip
    aria-label="More information"
    className={cn('inline-flex', adornmentClassName)}
    content={content}
  data-sentry-element="InfoTip" data-sentry-component="LabelInfoTooltip" data-sentry-source-file="details-field.tsx">
    <Info size={20} data-sentry-element="Info" data-sentry-source-file="details-field.tsx" />
  </InfoTip>
)

export const ExternalLinkTooltip = ({
  className,
  href,
  revealClassName = fieldRevealClassName,
  size = 20,
  target = '_blank',
  tooltip = 'Go to link',
}: {
  className?: string
  href: string
  revealClassName?: string
  size?: number
  target?: string
  tooltip?: string
}) => {
  return (
    <Tooltip data-sentry-element="Tooltip" data-sentry-component="ExternalLinkTooltip" data-sentry-source-file="details-field.tsx">
      <TooltipTrigger
        aria-label={tooltip}
        className={cn('inline-flex', adornmentBaseClassName, revealClassName, className)}
        render={<Link href={href} target={target} />}
      data-sentry-element="TooltipTrigger" data-sentry-source-file="details-field.tsx">
        <ExternalLink size={size} data-sentry-element="ExternalLink" data-sentry-source-file="details-field.tsx" />
      </TooltipTrigger>
      <TooltipContent data-sentry-element="TooltipContent" data-sentry-source-file="details-field.tsx">{tooltip}</TooltipContent>
    </Tooltip>
  )
}
